import {
  nextTick, onMounted, onUnmounted, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import router from '@/router';
import {
  captureEmbedOrganizationContext,
  EMBED_HEIGHT_SYNC_EVENT,
  EMBED_PATH_KEY,
  isCompactEmbedHeightRoute,
  isEmbeddedRoute,
  scrollEmbedToTop as postEmbedScrollToTop,
} from '@/utils/embeddedUtils';

/**
 * Sync iframe height with the parent window via postMessage.
 *
 * Measures the content container directly (not body/html) to avoid feedback loops
 * when the parent resizes the iframe incrementally.
 */
export function useIframeHeightSync() {
  const route = useRoute();

  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;
  let heightCheckInterval: number | null = null;
  let lastSentHeight = 0;
  let heightFloor = 0;
  let routeTransitionUntil = 0;
  let routeTransitionEndTimer: number | null = null;
  let sendDebounceTimer: number | null = null;
  let initTimer: number | null = null;
  let cleanupListener: (() => void) | null = null;
  let removeRouterGuard: (() => void) | null = null;
  let paymentElementObserved = false;

  const ROUTE_TRANSITION_MS = 2000;
  const MIN_STABLE_HEIGHT = 400;
  const EMBED_HEIGHT_KEY = 'spotlight-embed-last-height';

  const contentSelectors = [
    '.sticker-purchase-root',
    '.ticket-checkout-page',
    '.ticket-success-page',
    '.sticker-checkout-page',
    '.webStickerBody',
    '.profile-container',
    '.profile-content-container',
    '.spotlight_card',
    '.payment-layout-wrapper',
    '.complete-profile-container',
    'main',
    '[role="main"]',
    '#app',
    '[id="app"]',
    '.app-container',
  ];

  const fixedFooterSelectors = [
    '.payment-fixed-footer',
    '.payment-footer-spacer',
    '.checkout-footer',
    '.action-buttons-fixed',
  ];

  function findContentContainer(): HTMLElement | null {
    for (const selector of contentSelectors) {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) return element;
    }
    return null;
  }

  function measureElementBottom(el: HTMLElement): number {
    const rect = el.getBoundingClientRect();
    return Math.ceil(rect.bottom + window.scrollY);
  }

  function getFixedFooterBottom(): number {
    let maxBottom = 0;

    fixedFooterSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        maxBottom = Math.max(maxBottom, measureElementBottom(node as HTMLElement));
      });
    });

    return maxBottom;
  }

  function measureElementHeight(el: HTMLElement): number {
    const rect = el.getBoundingClientRect();
    const fromRect = Math.ceil(rect.bottom + window.scrollY);
    const fromScroll = Math.ceil(rect.top + window.scrollY + el.scrollHeight);
    return Math.max(fromRect, fromScroll, el.offsetHeight);
  }

  function getPaymentPageHeight(): number {
    const root = document.querySelector('.sticker-purchase-root') as HTMLElement | null;
    if (root) {
      return measureElementHeight(root);
    }

    const card = document.querySelector('.spotlight_card') as HTMLElement | null;
    const footer = document.querySelector('.payment-fixed-footer') as HTMLElement | null;

    if (card && footer) {
      return Math.max(measureElementHeight(card), measureElementHeight(footer));
    }

    if (card) {
      return measureElementHeight(card);
    }

    return 0;
  }

  function getContentHeight(): number {
    const isPaymentPage = route.path.includes('/payment/');

    if (isPaymentPage) {
      return getPaymentPageHeight();
    }

    const contentContainer = findContentContainer();
    const fixedFooterBottom = getFixedFooterBottom();

    if (contentContainer) {
      return Math.max(
        measureElementHeight(contentContainer),
        fixedFooterBottom,
      );
    }

    const { body } = document;
    return Math.max(measureElementHeight(body), fixedFooterBottom);
  }

  function getStoredEmbedHeight(): number {
    const stored = sessionStorage.getItem(EMBED_HEIGHT_KEY);
    return stored ? parseInt(stored, 10) || 0 : 0;
  }

  function persistEmbedHeight(height: number) {
    sessionStorage.setItem(EMBED_HEIGHT_KEY, String(height));
  }

  function isInRouteTransition(): boolean {
    return Date.now() < routeTransitionUntil;
  }

  function beginRouteTransition(targetPath?: string) {
    heightFloor = Math.max(lastSentHeight, getStoredEmbedHeight());

    const path = targetPath ?? route.path;
    if (isCompactEmbedHeightRoute(path)) {
      heightFloor = Math.min(heightFloor, 600);
    }

    routeTransitionUntil = Date.now() + ROUTE_TRANSITION_MS;

    if (routeTransitionEndTimer) {
      window.clearTimeout(routeTransitionEndTimer);
    }

    routeTransitionEndTimer = window.setTimeout(() => {
      routeTransitionEndTimer = null;
      routeTransitionUntil = 0;
      heightFloor = 0;
      scheduleHeightUpdate(true);
    }, ROUTE_TRANSITION_MS);
  }

  function endRouteTransitionEarly() {
    if (routeTransitionEndTimer) {
      window.clearTimeout(routeTransitionEndTimer);
      routeTransitionEndTimer = null;
    }

    routeTransitionUntil = 0;
    heightFloor = 0;
  }

  function notifyUrlChange(): boolean {
    if (window.self === window.parent) {
      return false;
    }

    const { href } = window.location;
    const lastPath = sessionStorage.getItem(EMBED_PATH_KEY);
    const isNavigation = !!lastPath && lastPath !== route.fullPath;

    sessionStorage.setItem(EMBED_PATH_KEY, route.fullPath);

    if (isNavigation) {
      scrollEmbedToTop();

      // Compact pages measure fresh — don't carry inflated heights from prior pages
      if (isCompactEmbedHeightRoute(route.path)) {
        sessionStorage.removeItem(EMBED_HEIGHT_KEY);
      }

      const minHeight = getStoredEmbedHeight();
      if (minHeight > 0) {
        window.parent.postMessage(
          {
            type: 'spotlight-iframe-min-height',
            minHeight,
          },
          '*',
        );
      }
    }

    window.parent.postMessage(
      {
        type: 'spotlight-iframe-url',
        url: href,
        path: route.fullPath,
        isNavigation,
      },
      '*',
    );

    return isNavigation;
  }

  function scrollEmbedToTop() {
    if (window.self === window.parent) {
      return;
    }

    postEmbedScrollToTop('route-change');
  }

  function sendHeightToParent(force = false) {
    if (window.self === window.parent) {
      return;
    }

    const measuredHeight = getContentHeight(force);
    let height = measuredHeight;
    const inTransition = isInRouteTransition();

    if (inTransition && measuredHeight < MIN_STABLE_HEIGHT) {
      if (heightFloor <= 0) {
        return;
      }

      height = heightFloor;
    } else if (inTransition) {
      height = Math.max(measuredHeight, heightFloor);

      // New page content loaded — allow growth and end the shrink guard
      if (measuredHeight > heightFloor + 50) {
        endRouteTransitionEarly();
        height = measuredHeight;
      } else if (
        measuredHeight < heightFloor - 50
        && measuredHeight >= MIN_STABLE_HEIGHT
      ) {
        // Shorter page loaded (e.g. org profile → sticker details)
        endRouteTransitionEarly();
        height = measuredHeight;
      }
    } else if (
      !force
      && measuredHeight < lastSentHeight - 20
      && !isCompactEmbedHeightRoute(route.path)
    ) {
      return;
    } else if (
      route.path.includes('/payment/')
      && paymentElementObserved
      && measuredHeight < lastSentHeight - 20
    ) {
      // Payment page: allow shrinking to actual content once Stripe has loaded
      height = measuredHeight;
    } else if (
      route.path.includes('/payment/')
      && !paymentElementObserved
      && measuredHeight < heightFloor
    ) {
      height = Math.max(measuredHeight, heightFloor);
    }

    if (!force && Math.abs(height - lastSentHeight) <= 5) {
      return;
    }

    const previousSentHeight = lastSentHeight;
    lastSentHeight = height;
    persistEmbedHeight(height);

    const scrollToTop = isCompactEmbedHeightRoute(route.path)
      && (force || height < previousSentHeight - 50);

    window.parent.postMessage(
      {
        type: 'spotlight-iframe-height',
        height,
        path: route.path,
        scrollToTop,
        reason: route.path.includes('/web/ticket/success')
          ? 'ticket-success-height'
          : undefined,
      },
      '*',
    );
  }

  function scheduleHeightUpdate(force = false) {
    if (force) {
      if (sendDebounceTimer) {
        window.clearTimeout(sendDebounceTimer);
        sendDebounceTimer = null;
      }
      sendHeightToParent(true);
      return;
    }

    if (sendDebounceTimer) {
      window.clearTimeout(sendDebounceTimer);
    }

    sendDebounceTimer = window.setTimeout(() => {
      sendDebounceTimer = null;
      sendHeightToParent();
    }, 300);
  }

  function observePaymentElementIfNeeded() {
    if (!route.path.includes('/payment/') || paymentElementObserved) {
      return;
    }

    const paymentElement = document.getElementById('payment-element');
    if (!paymentElement) {
      return;
    }

    const hasStripeContent = paymentElement.childElementCount > 0 || paymentElement.offsetHeight > 30;
    if (!hasStripeContent) {
      return;
    }

    paymentElementObserved = true;

    if (resizeObserver) {
      resizeObserver.observe(paymentElement);
    }

    [500, 1500, 3000].forEach((delay) => {
      window.setTimeout(() => scheduleHeightUpdate(true), delay);
    });
  }

  function setupResizeObserver() {
    cleanup();

    if (typeof ResizeObserver === 'undefined') {
      heightCheckInterval = window.setInterval(() => scheduleHeightUpdate(), 500);
      return;
    }

    let debounceTimer: number | null = null;

    resizeObserver = new ResizeObserver(() => {
      if (debounceTimer) {
        window.clearTimeout(debounceTimer);
      }

      debounceTimer = window.setTimeout(() => {
        scheduleHeightUpdate();
      }, 150);
    });

    const targetElement = findContentContainer() || document.body;

    resizeObserver.observe(targetElement);
    observePaymentElementIfNeeded();

    const paymentElement = document.getElementById('payment-element');
    if (paymentElement && resizeObserver && !paymentElementObserved) {
      resizeObserver.observe(paymentElement);
    }

    mutationObserver = new MutationObserver(() => {
      observePaymentElementIfNeeded();
      scheduleHeightUpdate();
    });

    mutationObserver.observe(targetElement, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    if (document.body !== targetElement) {
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }

  function setupRouterGuard() {
    removeRouterGuard = router.beforeEach((to, from) => {
      if (window.self === window.parent) {
        return true;
      }

      if (!from.name || from.fullPath === to.fullPath) {
        return true;
      }

      if (!isEmbeddedRoute(to.path) && !isEmbeddedRoute(from.path)) {
        return true;
      }

      beginRouteTransition(to.path);

      if (heightFloor > 0) {
        lastSentHeight = heightFloor;
        window.parent.postMessage(
          {
            type: 'spotlight-iframe-min-height',
            minHeight: heightFloor,
          },
          '*',
        );
      }

      return true;
    });
  }

  function handleRouteChange(_newPath: string, _oldPath?: string) {
    const isNavigation = notifyUrlChange();

    if (window.self !== window.parent) {
      captureEmbedOrganizationContext({
        fullPath: route.fullPath,
        path: route.path,
        query: route.query,
      });
    }

    if (isNavigation) {
      beginRouteTransition();
      lastSentHeight = heightFloor;
    }

    nextTick(() => {
      setupResizeObserver();
      scheduleHeightUpdate(true);

      window.setTimeout(() => scheduleHeightUpdate(true), 300);
      window.setTimeout(() => scheduleHeightUpdate(true), 800);
    });
  }

  function setupMessageListener() {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'spotlight-request-height') {
        scheduleHeightUpdate(true);
      }
    };

    const handleEmbedHeightSyncEvent = () => {
      if (isCompactEmbedHeightRoute(route.path)) {
        heightFloor = 0;
        routeTransitionUntil = 0;
      }

      observePaymentElementIfNeeded();
      scheduleHeightUpdate(true);
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener(EMBED_HEIGHT_SYNC_EVENT, handleEmbedHeightSyncEvent);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener(EMBED_HEIGHT_SYNC_EVENT, handleEmbedHeightSyncEvent);
    };
  }

  function cleanup() {
    if (routeTransitionEndTimer) {
      window.clearTimeout(routeTransitionEndTimer);
      routeTransitionEndTimer = null;
    }

    if (sendDebounceTimer) {
      window.clearTimeout(sendDebounceTimer);
      sendDebounceTimer = null;
    }

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }

    if (heightCheckInterval) {
      window.clearInterval(heightCheckInterval);
      heightCheckInterval = null;
    }

    paymentElementObserved = false;
  }

  function handleWindowLoad() {
    scheduleHeightUpdate(true);
  }

  onMounted(() => {
    if (window.self === window.parent) {
      return;
    }

    document.documentElement.classList.add('spotlight-embedded');

    initTimer = window.setTimeout(() => {
      setupResizeObserver();
      cleanupListener = setupMessageListener();
      setupRouterGuard();

      const isNavigation = notifyUrlChange();
      if (isNavigation) {
        beginRouteTransition();
        lastSentHeight = heightFloor;
      } else {
        const storedHeight = getStoredEmbedHeight();
        if (storedHeight > 0) {
          lastSentHeight = storedHeight;
        }
      }

      captureEmbedOrganizationContext({
        fullPath: route.fullPath,
        path: route.path,
        query: route.query,
      });

      scheduleHeightUpdate(true);
      window.addEventListener('load', handleWindowLoad);
    }, 100);

    watch(
      () => route.fullPath,
      (newPath, oldPath) => handleRouteChange(newPath, oldPath),
    );
  });

  onUnmounted(() => {
    if (initTimer) {
      window.clearTimeout(initTimer);
      initTimer = null;
    }

    document.documentElement.classList.remove('spotlight-embedded');
    cleanup();
    cleanupListener?.();
    cleanupListener = null;
    removeRouterGuard?.();
    removeRouterGuard = null;
    window.removeEventListener('load', handleWindowLoad);
  });
}
